/**
 * Items to review for a paycheck in parts (TMMAPP-0173 items 3 and 4; TMMAPI-0202, MM-D-153).
 * - `PARTIAL_OVERDUE` (tmm-api's G2): a part-paid occurrence past its date and window. It reads
 *   "{series}: {received} of {amount} received" off the occurrence's own served view.
 * - A part suggestion is a `CONFIRM_MATCH` that names the occurrence and the line (`seriesId`,
 *   `originalOn`, `transactionId`; tmm-api's G1). The full-match `CONFIRM_MATCH` carries candidates
 *   instead. It reads "{amount} on {date} looks like part of {series}". Confirm is the existing
 *   `…/match`, never automatic (MM-D-45).
 */
import type { TFunction } from 'i18next';
import type { Account } from '@/api/accounts';
import { plannedApi, type Occurrence } from '@/api/planned';
import type { ReviewItem } from '@/api/review';
import { transactionsApi } from '@/api/transactions';
import { lineAmount, isOutgoing } from '@/features/planned/paycheckParts';
import { formatCivilDate } from '@/i18n/format';

/** A `CONFIRM_MATCH` that suggests one line as part of one occurrence. */
export function isPartSuggestion(item: ReviewItem): boolean {
  return (
    item.kind === 'CONFIRM_MATCH' &&
    item.seriesId !== null &&
    item.originalOn !== null &&
    item.transactionId !== null
  );
}

export interface PartialCopy {
  copy: string;
  /** The occurrence as served (a `PARTIAL_OVERDUE` item's "That's all" names its received). */
  occurrence: Occurrence | null;
}

/** The item's description line, read from what tmm-api serves; `null` for any other item. */
export async function partialPaycheckCopy(
  item: ReviewItem,
  accounts: readonly Account[],
  fallbackCurrency: string | null,
  t: TFunction,
  locale: string,
): Promise<PartialCopy | null> {
  const seriesId = item.seriesId;
  const originalOn = item.originalOn;
  if (seriesId === null || originalOn === null) return null;
  const account = accounts.find((row) => row.id === item.accountId);
  const currency = account?.currency ?? account?.operatingCurrency ?? fallbackCurrency;
  if (item.kind === 'PARTIAL_OVERDUE') {
    const [timeline, series] = await Promise.all([
      plannedApi.timeline(seriesId, { from: originalOn, to: originalOn }),
      plannedApi.getSeries(seriesId).catch(() => null),
    ]);
    const occurrence = timeline.find((row) => row.originalOn === originalOn) ?? timeline[0] ?? null;
    const name = series?.name ?? occurrence?.payee ?? '';
    if (occurrence === null || occurrence.receivedMinor === null) return { copy: name, occurrence };
    const values = {
      series: name,
      received: lineAmount(occurrence.receivedMinor, currency, locale),
      amount: lineAmount(occurrence.amountMinor, currency, locale),
    };
    return {
      copy: isOutgoing(occurrence.amountMinor)
        ? t('review.partialPaid', values)
        : t('review.partialReceived', values),
      occurrence,
    };
  }
  if (isPartSuggestion(item) && item.transactionId !== null) {
    const [line, series] = await Promise.all([
      transactionsApi.get(item.accountId, item.transactionId),
      plannedApi.getSeries(seriesId).catch(() => null),
    ]);
    return {
      copy: t('review.partSuggestion', {
        amount: lineAmount(line.amountMinor, line.currency || currency, locale),
        date: formatCivilDate(line.postedOn, locale),
        series: series?.name ?? line.payee,
      }),
      occurrence: null,
    };
  }
  return null;
}
